import type { LegalEntityFormData, LegalEntityFormErrors } from "../../types/legalEntity";

/** National digits only: strip non-digits; if 11 digits starting with 1, use last 10 (NANP). */
export function normalizeNationalPhoneDigits(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.slice(1);
  }
  return digits;
}

/** Format phone for API payload: +1 plus 10 national digits (e.g. +19839124812). */
export function formatPhoneForApiPayload(raw: string): string {
  const national = normalizeNationalPhoneDigits(raw.trim());
  return `+1${national}`;
}

const LOCAL_PART_ALLOWED = /^[a-zA-Z0-9._%+-]+$/;

/** Unicode letters and ASCII spaces only (no digits, punctuation, or symbols). */
const LEGAL_ENTITY_LETTERS_SPACES_PATTERN = /^[\p{L} ]+$/u;

export const LEGAL_ENTITY_LETTERS_SPACES_ERROR =
  "Use letters and spaces only. Numbers and special characters are not allowed.";

/** Validates name, title, and company (entity name) fields. */
export const getLegalEntityLettersOnlyFieldError = (raw: string): string | undefined => {
  const t = raw.trim();
  if (t.length === 0) {
    return undefined;
  }
  if (!LEGAL_ENTITY_LETTERS_SPACES_PATTERN.test(t)) {
    return LEGAL_ENTITY_LETTERS_SPACES_ERROR;
  }
  return undefined;
};

/** Single-field validation for interactive blur/change feedback (phone). FR-003: 10-digit U.S. NANP. */
export const getPhoneFieldError = (raw: string): string | undefined => {
  const v = raw.trim();
  if (!v) return "Phone is required.";
  const national = normalizeNationalPhoneDigits(v);
  if (national.length !== 10) return "Enter a valid phone number.";
  return undefined;
};

/** Single-field validation for interactive blur/change feedback (email). FR-004. */
export const getEmailFieldError = (raw: string): string | undefined => {
  const v = raw.trim();
  if (!v) return "Email is required.";
  const at = v.indexOf("@");
  const lastAt = v.lastIndexOf("@");
  if (at <= 0 || at !== lastAt) return "Enter a valid email address.";
  const local = v.slice(0, at);
  const domain = v.slice(at + 1);
  if (!domain.includes(".")) return "Enter a valid email address.";
  const tld = domain.slice(domain.lastIndexOf(".") + 1);
  if (tld.length < 2 || !/^[a-zA-Z]+$/.test(tld)) return "Enter a valid email address.";
  if (!LOCAL_PART_ALLOWED.test(local)) return "Enter a valid email address.";
  return undefined;
};

export const getZipFieldError = (raw: string): string | undefined => {
  const v = raw.trim();
  if (!v) return "Zip is required.";
  if (!/^\d{1,5}$/.test(v)) return "Enter up to 5 digits.";
  return undefined;
};

const requiredFieldEntries: ReadonlyArray<[keyof LegalEntityFormErrors, string]> = [
  ["name", "Name is required."],
  ["title", "Title is required."],
  ["company", "Company is required."],
  ["streetAddress", "Street Address is required."],
  ["city", "City is required."],
  ["state", "State is required."],
  ["zip", "Zip is required."]
];

export const validateLegalEntityForm = (formData: LegalEntityFormData): LegalEntityFormErrors => {
  const errors: LegalEntityFormErrors = {};

  for (const [field, message] of requiredFieldEntries) {
    const value = formData[field];
    if (!value?.trim()) {
      errors[field] = message;
    }
  }

  const phoneErr = getPhoneFieldError(formData.phone);
  if (phoneErr) {
    errors.phone = phoneErr;
  }

  const emailErr = getEmailFieldError(formData.email);
  if (emailErr) {
    errors.email = emailErr;
  }

  const zipErr = getZipFieldError(formData.zip);
  if (zipErr) {
    errors.zip = zipErr;
  }

  const nameLetters = getLegalEntityLettersOnlyFieldError(formData.name);
  if (nameLetters) {
    errors.name = nameLetters;
  }
  const titleLetters = getLegalEntityLettersOnlyFieldError(formData.title);
  if (titleLetters) {
    errors.title = titleLetters;
  }
  const companyLetters = getLegalEntityLettersOnlyFieldError(formData.company);
  if (companyLetters) {
    errors.company = companyLetters;
  }

  return errors;
};

const addLegalEntityRequiredFieldEntries: ReadonlyArray<[keyof LegalEntityFormErrors, string]> = [
  ["company", "Company is required."],
  ["streetAddress", "Street Address is required."],
  ["city", "City is required."],
  ["state", "State is required."],
  ["zip", "Zip is required."]
];

/** Validates Add Legal Entity drawer fields only (company + address; no contact fields). */
export const validateAddLegalEntityForm = (
  formData: LegalEntityFormData
): LegalEntityFormErrors => {
  const errors: LegalEntityFormErrors = {};

  for (const [field, message] of addLegalEntityRequiredFieldEntries) {
    const value = formData[field];
    if (!value?.trim()) {
      errors[field] = message;
    }
  }

  const zipErr = getZipFieldError(formData.zip);
  if (zipErr) {
    errors.zip = zipErr;
  }

  return errors;
};
